import React, { useState, useEffect } from 'react';
import { getAttendanceUsers, updateUser } from './attendanceApi';
import apiClient from '../../api';
import { User as UserIcon, Edit3, Save, X, Shield, Briefcase, Truck, CheckCircle2, XCircle, Activity, UserPlus, Search, Filter, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import useAuthStore, { SUPER_ADMIN_EMAILS } from '../../store/useAuthStore';

export function UserManagement() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // Cambiado de 'active' a 'all' por defecto
  
  const [editForm, setEditForm] = useState({ name: '', role: '', is_active: true, password: '', linked_provider_id: null });
  const [createForm, setCreateForm] = useState({ email: '', name: '', password: '', role: 'locutor', linked_provider_id: null });
  const [providers, setProviders] = useState([]);

  const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(currentUser?.email?.trim()?.toLowerCase());

  useEffect(() => {
    fetchUsers();
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const res = await apiClient.get('/transport/providers/');
      setProviders(res.data || []);
    } catch (error) {
      console.error("Error al cargar proveedores:", error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAttendanceUsers(1000);
      const filtered = data.filter(u => u.role !== 'passenger' && u.role !== 'requester');
      setUsers(filtered);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setEditForm({
      name: user.name || '',
      role: user.role || 'locutor',
      is_active: user.is_active,
      linked_provider_id: user.linked_provider_id || null,
      permissions: typeof user.permissions === 'object' && user.permissions !== null ? user.permissions : {},
      password: '' // Inicialmente vacío para no cambiarla a menos que se escriba algo
    });
  };

  const handleSave = async (id) => {
    try {
      // Solo enviamos la contraseña si se escribió algo nuevo
      const dataToSend = { ...editForm };
      if (!dataToSend.password) delete dataToSend.password;

      await updateUser(id, dataToSend);
      toast.success("Usuario actualizado");
      setEditingId(null);
      fetchUsers();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/users/', createForm);
      toast.success("Usuario creado");
      setShowCreateForm(false);
      setCreateForm({ email: '', name: '', password: '', role: 'locutor' });
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail || "Error al crear";
      toast.error(typeof detail === 'string' ? detail : "Error al crear");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield size={14} className="text-slate-500" />;
      case 'driver': return <Truck size={14} className="text-slate-500" />;
      case 'gestor': return <Briefcase size={14} className="text-slate-500" />;
      case 'locutor': return <Activity size={14} className="text-slate-500" />;
      case 'operaciones': return <Activity size={14} className="text-blue-500" />;
      case 'provider': return <Briefcase size={14} className="text-purple-500" />;
      default: return <Briefcase size={14} className="text-slate-400" />;
    }
  };

  // Filtrado por búsqueda y por estado (Activo/Inactivo)
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.role?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === 'active') return matchesSearch && u.is_active;
    if (statusFilter === 'inactive') return matchesSearch && !u.is_active;
    return matchesSearch;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* CAMPOS SEÑUELO: Engañan al navegador para que rellene esto y no el buscador real */}
      <div style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
        <input type="text" name="fake_user_prevent_autofill" tabIndex="-1" />
        <input type="password" name="fake_password_prevent_autofill" tabIndex="-1" autoComplete="new-password" />
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 px-2">
        <div>
          <h2 className="text-2xl font-black text-slate-800 flex items-center gap-3 m-0">
            <UserIcon size={28} className="text-slate-400" /> Personal {loading && <span className="text-xs text-blue-500 animate-pulse ml-2 font-black">Cargando...</span>}
          </h2>
          <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">Gestión de Cuentas y Accesos ({filteredUsers.length} encontrados)</p>
        </div>
        
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          {/* FILTRO DE ESTADO */}
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-xs font-bold outline-none focus:border-slate-400 shadow-sm transition-all"
            >
              <option value="active">Solo Activos</option>
              <option value="inactive">Solo Inactivos</option>
              <option value="all">Ver Todos</option>
            </select>
          </div>

          {/* BARRA DE BÚSQUEDA */}
          <div className="relative flex-grow md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input 
              type="search" 
              name="field_search_attendance_prevent"
              autoComplete="one-time-code"
              placeholder="Buscar por nombre, email o rol..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs outline-none focus:border-slate-400 shadow-sm transition-all"
            />
          </div>

          <button 
            onClick={() => setShowCreateForm(!showCreateForm)}
            className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all ${
              showCreateForm ? 'bg-slate-200 text-slate-600' : 'bg-slate-800 text-white shadow-lg shadow-slate-200'
            }`}
          >
            {showCreateForm ? <><X size={16}/> Cancelar</> : <><UserPlus size={16}/> Nuevo Usuario</>}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-xl animate-in slide-in-from-top-4">
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Email</label>
              <input required type="email" value={createForm.email} onChange={(e) => setCreateForm({...createForm, email: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-slate-400 shadow-sm" placeholder="email@ejemplo.com" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Nombre Real</label>
              <input required type="text" value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-slate-400 shadow-sm" placeholder="Nombre completo" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Contraseña</label>
              <input required type="password" value={createForm.password} onChange={(e) => setCreateForm({...createForm, password: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-slate-400 shadow-sm" placeholder="••••••••" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Rol</label>
              <div className="flex gap-2">
                <select value={createForm.role} onChange={(e) => setCreateForm({...createForm, role: e.target.value, linked_provider_id: e.target.value === 'provider' ? createForm.linked_provider_id : null})} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-slate-400 shadow-sm">
                  {isSuperAdmin && <option value="admin">Administrador</option>}
                  {isSuperAdmin && <option value="gestor">Gestor</option>}
                  {isSuperAdmin && <option value="operaciones">Jefa de Operaciones</option>}
                  <option value="locutor">Locutor</option>
                  <option value="driver">Conductor</option>
                  <option value="provider">Proveedor</option>
                </select>
                <button type="submit" className="bg-slate-800 text-white p-3 rounded-xl hover:bg-slate-900 shadow-lg shadow-slate-200"><Save size={20}/></button>
              </div>
            </div>
            {createForm.role === 'provider' && (
              <div className="space-y-1 col-span-1 md:col-span-4 animate-in fade-in duration-300">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Asociar Proveedor</label>
                <select 
                  required 
                  value={createForm.linked_provider_id || ''} 
                  onChange={(e) => setCreateForm({...createForm, linked_provider_id: e.target.value ? parseInt(e.target.value) : null})} 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-slate-400 shadow-sm"
                >
                  <option value="">Seleccione un Proveedor...</option>
                  {providers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}
          </form>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] bg-slate-50">
                <th className="px-10 py-6">Trabajador / Email</th>
                <th className="px-8 py-6">Nombre Real</th>
                <th className="px-8 py-6">Rol</th>
                <th className="px-8 py-6 text-center">Estado</th>
                <th className="px-10 py-6 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-10 py-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
                        <UserIcon size={20} />
                      </div>
                      <div>
                        <p className="font-bold text-slate-700 m-0">{user.email}</p>
                        <p className="text-[10px] text-slate-400 uppercase font-black mt-0.5">ID: #{user.id}</p>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-8 py-6">
                    {editingId === user.id ? (
                      <input type="text" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="bg-white text-slate-800 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full outline-none focus:border-slate-400 shadow-sm" />
                    ) : (
                      <span className={`text-sm font-bold ${user.name ? 'text-slate-600' : 'text-slate-300 italic'}`}>{user.name || 'Sin nombre'}</span>
                    )}
                  </td>

                  <td className="px-8 py-6">
                    {editingId === user.id ? (
                      <div className="space-y-2">
                        <select value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value, linked_provider_id: e.target.value === 'provider' ? editForm.linked_provider_id : null})} className="bg-white text-slate-800 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full outline-none focus:border-slate-400 shadow-sm">
                          {isSuperAdmin && <option value="admin">Administrador</option>}
                          {isSuperAdmin && <option value="gestor">Gestor</option>}
                          {isSuperAdmin && <option value="operaciones">Jefa de Operaciones</option>}
                          <option value="locutor">Locutor</option>
                          <option value="driver">Conductor</option>
                          <option value="provider">Proveedor</option>
                        </select>
                        {editForm.role === 'provider' && (
                          <select 
                            required 
                            value={editForm.linked_provider_id || ''} 
                            onChange={(e) => setEditForm({...editForm, linked_provider_id: e.target.value ? parseInt(e.target.value) : null})} 
                            className="bg-white text-slate-800 border border-slate-200 rounded-lg px-3 py-2 text-xs w-full outline-none focus:border-slate-400 shadow-sm"
                          >
                            <option value="">Asociar Proveedor...</option>
                            {providers.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        )}
                        <div className="relative">
                          <Lock size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input 
                            type="password" 
                            placeholder="Cambiar clave..." 
                            value={editForm.password}
                            onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                            className="bg-white text-slate-800 border border-slate-200 rounded-lg pl-7 pr-3 py-2 text-[10px] w-full outline-none focus:border-slate-400 shadow-sm"
                          />
                        </div>
                        <label className="flex items-center gap-1.5 mt-1.5 text-[11px] font-semibold text-slate-600 cursor-pointer select-none">
                          <input 
                            type="checkbox"
                            checked={!!editForm.permissions?.can_process_payroll}
                            onChange={(e) => setEditForm({
                              ...editForm,
                              permissions: {
                                ...(editForm.permissions || {}),
                                can_process_payroll: e.target.checked
                              }
                            })}
                            className="rounded border-slate-300 text-slate-800 focus:ring-0"
                          />
                          <span>🔐 Liquidaciones Sueldo</span>
                        </label>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full w-fit">
                        {getRoleIcon(user.role)}
                        <span className="text-[10px] font-black uppercase text-slate-500">{user.role}</span>
                      </div>
                    )}
                  </td>

                  <td className="px-8 py-6 text-center">
                    {editingId === user.id ? (
                      <button onClick={() => setEditForm({...editForm, is_active: !editForm.is_active})} className={`p-2 rounded-lg ${editForm.is_active ? 'bg-slate-100 text-slate-600' : 'bg-rose-50 text-rose-600'}`}>
                        {editForm.is_active ? <CheckCircle2 size={20}/> : <XCircle size={20}/>}
                      </button>
                    ) : (
                      <div className="flex justify-center">
                        {user.is_active ? <CheckCircle2 size={18} className="text-slate-300" /> : <XCircle size={18} className="text-rose-200" />}
                      </div>
                    )}
                  </td>

                  <td className="px-10 py-6 text-right">
                    {editingId === user.id ? (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleSave(user.id)} className="p-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 shadow-md"><Save size={18} /></button>
                        <button onClick={() => setEditingId(null)} className="p-2 bg-slate-100 text-slate-400 rounded-lg hover:bg-slate-200"><X size={18} /></button>
                      </div>
                    ) : (
                      <button onClick={() => handleEdit(user)} className="p-2 bg-slate-50 text-slate-400 rounded-lg hover:bg-slate-800 hover:text-white transition-all border border-slate-100">
                        <Edit3 size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-20">
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs italic">No se encontraron usuarios</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
